/**
 * AuditTimeline — the ONE shared "roadmap timeline" view of audit rows
 * (CLAUDE.md §18.6 — don't reinvent).  Used by BOTH the full-page Data Center
 * audit log (AuditTrailPage) AND the per-form <AuditTrailButton> popup, so the
 * timeline looks and behaves identically everywhere.
 *
 * Groups the rows by calendar day (local, via the shared fmtLocalDate helper),
 * newest-first, colour-coded by operation (green created · blue value-changed ·
 * red deleted).
 */
import { useMemo } from 'react';
import { fmtLocalDate, fmtLocalDateTime } from '../../utils/datetime';

/** Minimal audit-row shape the timeline needs (structurally shared by the
 *  full-page and popup row types). */
export interface AuditTimelineRow {
  oid:            string;
  createdDate?:   string | null;
  createdBy?:     string | null;
  operationType?: string | null;
  formName?:      string | null;
  recordId?:      string | null;
  propertyName?:  string | null;
  oldValue?:      string | null;
  newValue?:      string | null;
}

/** Operation → colour variant.  Drives BOTH the grid Action pill and the
 *  Timeline: created green, value-changed blue, deleted red. */
export function opVariant(op?: string | null): 'ok' | 'warn' | 'alert' | 'info' {
  const k = (op ?? '').toLowerCase();
  if (k.includes('add') || k.includes('insert') || k.includes('creat')) return 'ok';
  if (k.includes('update') || k.includes('edit') || k.includes('chang')) return 'info';
  if (k.includes('delete') || k.includes('remove')) return 'alert';
  return 'info';
}

type OpColor = { dot: string; bg: string; text: string };
// token-exempt: chart — operation-coded series colours (created green · value-changed
// blue · deleted red · amber) with no matching --ai-* token.
const OP_INFO: OpColor = { dot: '#185FA5', bg: '#E6F1FB', text: '#0C447C' }; // Value changed — blue
export const OP_COLORS: Record<string, OpColor> = {
  ok:    { dot: '#3B6D11', bg: '#EAF3DE', text: '#27500A' }, // Created — green
  info:  OP_INFO,
  alert: { dot: '#A32D2D', bg: '#FCEBEB', text: '#791F1F' }, // Deleted — red
  warn:  { dot: '#854F0B', bg: '#FAEEDA', text: '#633806' }, // amber (legacy/other)
};

/** Initials for the "Changed By" avatar (e.g. "Service" → "SE", "E174807" → "E1"). */
function initials(name?: string | null): string {
  const s = (name ?? '').trim();
  if (!s) return '—';
  const p = s.split(/\s+/);
  const a = p[0] ?? '';
  const b = p.length > 1 ? (p[1] ?? '') : '';
  return (b ? (a[0] ?? '') + (b[0] ?? '') : s.slice(0, 2)).toUpperCase();
}

/** HH:mm from the shared datetime helper (no inline date formatting — §18.6). */
function timeOnly(iso?: string | null): string {
  const s = iso ? (fmtLocalDateTime(iso) || '') : '';
  const parts = s.split(' ');
  return parts.length > 1 ? (parts[parts.length - 1] ?? s) : s;
}

interface Props {
  rows: AuditTimelineRow[];
  loading?: boolean;
  emptyText?: string;
}

export function AuditTimeline({ rows, loading, emptyText = 'No audit entries.' }: Props) {
  const groups = useMemo(() => {
    const map = new Map<string, AuditTimelineRow[]>();
    for (const r of rows) {
      const day = r.createdDate ? (fmtLocalDate(r.createdDate) || '—') : '—';
      const arr = map.get(day) ?? [];
      if (arr.length === 0) map.set(day, arr);
      arr.push(r);
    }
    return Array.from(map.entries()).map(([day, items]) => ({ day, items }));
  }, [rows]);

  return (
    <div className="atl-tl">
      {loading ? (
        <div className="bl-empty">Loading…</div>
      ) : groups.length === 0 ? (
        <div className="bl-empty">{emptyText}</div>
      ) : groups.map(g => (
        <div key={g.day} className="atl-grp">
          <div className="atl-day">{g.day}</div>
          <div className="atl-track">
            {g.items.map((r, i) => {
              const c = OP_COLORS[opVariant(r.operationType)] ?? OP_INFO;
              return (
                <div className="atl-entry" key={`${r.oid}-${i}`}>
                  <span className="atl-node" style={{ background: c.dot }} />
                  <div className="atl-card">
                    <div className="atl-head">
                      <span className="atl-pill" style={{ background: c.bg, color: c.text }}>
                        {r.operationType ?? '—'}
                      </span>
                      {r.propertyName && <span className="atl-field">{r.propertyName}</span>}
                      {r.recordId && <span className="atl-rec">· {r.recordId}</span>}
                      <span style={{ flex: 1 }} />
                      <span className="atl-time">{timeOnly(r.createdDate)}</span>
                    </div>
                    {(r.oldValue || r.newValue) && (
                      <div className="atl-diff">
                        {r.oldValue && <span className="atl-old" title={r.oldValue}>{r.oldValue}</span>}
                        {r.oldValue && r.newValue && <i className="ti ti-arrow-right atl-arr" aria-hidden="true" />}
                        {r.newValue && <span className="atl-new" title={r.newValue}>{r.newValue}</span>}
                      </div>
                    )}
                    <div className="atl-who">
                      <span className="atl-av">{initials(r.createdBy)}</span>
                      <span>{r.createdBy || '—'}{r.formName ? ' · ' + r.formName : ''}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      <style>{`
        .atl-tl{padding:14px 16px;overflow-y:auto;flex:1}
        .atl-day{font-size:12px;font-weight:600;color:var(--ai-muted);margin:8px 0 8px}
        .atl-track{position:relative;padding-left:26px}
        .atl-track:before{content:'';position:absolute;left:9px;top:4px;bottom:4px;width:2px;background:var(--ai-line)}
        .atl-entry{position:relative;margin-bottom:12px}
        .atl-node{position:absolute;left:-22px;top:13px;width:13px;height:13px;border-radius:50%;border:2px solid #fff;box-shadow:0 0 0 1px var(--ai-line)}
        .atl-card{background:#fff;border:1px solid var(--ai-line-soft);border-radius:8px;padding:10px 12px}
        .atl-head{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
        .atl-pill{font-size:11px;font-weight:600;padding:2px 8px;border-radius:6px}
        .atl-field{font-size:13px;font-weight:600;color:var(--ai-ink)}
        .atl-rec{font-size:11px;color:var(--ai-muted)}
        .atl-time{font-size:12px;color:var(--ai-muted)}
        .atl-diff{margin:8px 0;font-size:13px;display:flex;align-items:center;gap:8px;flex-wrap:wrap}
        /* token-exempt: chart — old/new diff + avatar use the audit series colours (match OP_COLORS). */
        .atl-old{background:#FCEBEB;color:#791F1F;text-decoration:line-through;padding:1px 6px;border-radius:4px;max-width:340px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        .atl-new{background:#EAF3DE;color:#27500A;padding:1px 6px;border-radius:4px;max-width:340px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        .atl-arr{color:var(--ai-muted)}
        .atl-who{display:flex;align-items:center;gap:6px;font-size:12px;color:var(--ai-muted);margin-top:2px}
        .atl-av{width:20px;height:20px;border-radius:50%;background:#e6f1fb;color:#0c447c;display:inline-flex;align-items:center;justify-content:center;font-size:10px;font-weight:600;flex:none}
      `}</style>
    </div>
  );
}
